import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Card, Divider, DatePicker, Space, message, Empty, Select } from "antd"
import {
    ClockCircleOutlined, CalendarOutlined, CoffeeOutlined, ReloadOutlined,
} from "@ant-design/icons"
import moment from "moment"
import {
    LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, BarChart, Bar, Legend,
} from "recharts"
import { dateFormat, translations } from "../../utils/constants"
import { secureApi } from "../../Config/api"
import { useHistory } from "react-router-dom"
import { GET } from "../../utils/apis"
import { setAnalyticsData } from "../../Redux/actions/analyticsActions"
import paths from "../../Config/paths"

const { RangePicker } = DatePicker

const Analytics = ({ language }) => {
    const dispatch = useDispatch()
    const history = useHistory()

    const user = useSelector((state) => state.authReducer?.user)
    const employeeNameList = useSelector((state) => state.employeeLogReducer.nameList)
    const user_id = user?.id
    const analyticsData = useSelector((state) => state.analytics.data)
    const firstDay = moment().startOf("month")
    const today = moment()
    const [dateRange, setDateRange] = useState([firstDay, today])
    const [selectedOption, setSelectedOption] = useState([])

    useEffect(() => {
        const allowedRoles = ['admin', 'superAdmin', 'user'];
        if (user && !allowedRoles.includes(user.role)) {
            history.push(paths?.HOME);
        }
    }, [user, history]);


    useEffect(() => {
        fetchAnalytics()
    }, [dateRange, selectedOption])

    const fetchAnalytics = async () => {
        try {
            if (!user_id) {
                message.error("User ID missing");
                return;
            }

            const params = {
                user_id,
                role: user?.role,
                dateRange: JSON.stringify({
                    start: dateRange[0].format("YYYY-MM-DD"),
                    end: dateRange[1].format("YYYY-MM-DD"),
                }),
            };

            if ((user?.role === "admin" || user?.role === "superAdmin") && selectedOption.length > 0) {
                params.selectedUsers = selectedOption;
            }

            // console.log("📊 Fetching Analytics with params:", params);
            const response = await secureApi.get(GET?.GET_EMPLOYEE_ANALYTICS, { params });
            // console.log(response, "response");

            if (response?.success) {
                // 🧮 Calculate worked_hours and worked_hours_str
                const workedWithDurations = response?.charts?.workedHours.map((record) => {
                    const workDay = record?.date;
                    const entryDateTime = record?.entry_time ? `${workDay}T${record?.entry_time}` : null;
                    const exitDateTime = record?.exit_time ? `${workDay}T${record?.exit_time}` : null;

                    const entryMoment = entryDateTime ? moment.tz(entryDateTime, "Europe/Rome") : null;
                    const nowMoment = moment.tz("Europe/Rome");
                    const hasActiveBreak = record?.breaks?.some(b => b.start_time && !b.end_time);

                    const exitMoment = exitDateTime
                        ? moment.tz(exitDateTime, "Europe/Rome")
                        : hasActiveBreak
                            ? moment.tz(`${workDay}T${record?.breaks[record?.breaks.length - 1].start_time}`, "Europe/Rome")
                            : nowMoment;

                    if (!entryMoment) {
                        return { ...record, worked_hours: 0, worked_hours_str: "0h 0m" };
                    }

                    // 🕒 Calculate total break seconds
                    const totalBreakSeconds = record.breaks.reduce((acc, b) => {
                        if (b.start_time && b.end_time) {
                            const start = moment.tz(`${workDay}T${b.start_time}`, "Europe/Rome");
                            const end = moment.tz(`${workDay}T${b.end_time}`, "Europe/Rome");
                            return acc + end.diff(start, "seconds");
                        }
                        return acc;
                    }, 0);

                    // 🧭 Net worked time
                    const diffSeconds = Math.max(0, exitMoment.diff(entryMoment, "seconds") - totalBreakSeconds);
                    const h = Math.floor(diffSeconds / 3600);
                    const m = Math.floor((diffSeconds % 3600) / 60);

                    return {
                        ...record,
                        worked_hours: parseFloat((diffSeconds / 3600).toFixed(2)),
                        worked_hours_str: `${h}h ${m}m`,
                    };
                });

                // ✅ Merge duplicate dates — combine worked_hours_str properly
                const mergedByDate = workedWithDurations.reduce((acc, rec) => {
                    const existing = acc.find(item => item.date === rec.date);
                    if (existing) {
                        // Convert "xh ym" → total minutes
                        const toMinutes = (str) => {
                            const match = str.match(/(\d+)h\s*(\d+)m/);
                            return match ? parseInt(match[1]) * 60 + parseInt(match[2]) : 0;
                        };

                        const totalMinutes =
                            toMinutes(existing.worked_hours_str) + toMinutes(rec.worked_hours_str);

                        const h = Math.floor(totalMinutes / 60);
                        const m = totalMinutes % 60;

                        existing.worked_hours_str = `${h}h ${m}m`;
                        existing.worked_hours += rec.worked_hours || 0;
                        existing.breaks = [...(existing.breaks || []), ...(rec.breaks || [])];
                    } else {
                        acc.push({ ...rec });
                    }
                    return acc;
                }, []);

                // ✅ Assign merged data
                response.charts.workedHours = mergedByDate;

                // ✅ Cards summary
                const totalHours = mergedByDate.reduce((acc, rec) => acc + (rec.worked_hours || 0), 0);
                const avgDailyHours =
                    mergedByDate.length > 0 ? totalHours / mergedByDate.length : 0;

                response.cards = {
                    ...response.cards,
                    totalHoursWorked: parseFloat(totalHours.toFixed(2)),
                    avgDailyHours: parseFloat(avgDailyHours.toFixed(2)),
                };

                dispatch(setAnalyticsData(response));
            } else {
                message.error(response?.message || "Failed to fetch analytics");
            }
        } catch (error) {
            console.error("❌ Analytics API Error:", error);
            message.error("Error fetching analytics data");
        }
    };


    const CustomWorkedTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const value = Number(payload[0]?.value) || 0
            return (
                <div
                    style={{
                        background: "#fff",
                        border: "1px solid #ddd",
                        padding: "8px 12px",
                        borderRadius: 8,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                    }}
                >
                    <p style={{ margin: 0, fontWeight: 600 }}>{label}</p>
                    <p style={{ margin: 0, color: "#1890ff" }}>
                        {translations[language]?.workedHours}
                        : <b>{formatHours(value)}</b>
                    </p>

                </div>
            )
        }
        return null
    }

    const CustomBreakTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const value = Number(payload[0]?.value) || 0;

            // Convert minutes → hours & minutes
            const hours = Math.floor(value / 60);
            const minutes = Math.floor(value % 60);

            return (
                <div
                    style={{
                        background: "#fff",
                        border: "1px solid #ddd",
                        padding: "8px 12px",
                        borderRadius: 8,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                    }}
                >
                    <p style={{ margin: 0, fontWeight: 600 }}>{label}</p>
                    <p style={{ margin: 0, color: "#fa8c16" }}>
                        {translations[language]?.breakMinutes} : <b>{hours} hr {minutes} {translations[language]?.mint}</b>
                    </p>
                </div>
            );
        }
        return null;
    };


    const formatHours = (decimalHours) => {
        if (!decimalHours || isNaN(decimalHours)) return `0 hr 0 ${translations[language]?.mint}`;
        const hours = Math.floor(decimalHours);
        const minutes = Math.round((decimalHours - hours) * 60);
        return `${hours} hr ${minutes} ${translations[language]?.mint}`;
    };


    const cards = [
        {
            title: translations[language]?.totalWorkHours,
            value: formatHours(analyticsData?.cards?.totalHoursWorked),
            icon: (
                <div className="total-hours">
                    <ClockCircleOutlined className="card-icon" style={{ color: "#fff", fontSize: 24 }} />
                </div>
            ),
        },
        {
            title: translations[language]?.averageWorkHours,
            value: formatHours(analyticsData?.cards?.avgDailyHours),
            icon: (
                <div className="daily-average">
                    <CalendarOutlined className="card-icon" style={{ color: "#fff", fontSize: 24 }} />
                </div>
            ),
        },
        {
            title: translations[language]?.breakTaken,

            value: `${analyticsData?.cards?.breaksTaken || 0}`,
            icon: (
                <div className="total-expenses">
                    <CoffeeOutlined className="card-icon" style={{ color: "#fff", fontSize: 24 }} />
                </div>
            ),
        },
        {
            title: translations[language]?.resumesBreak,

            value: `${analyticsData?.cards?.resumes || 0}`,
            icon: (
                <div className="extra-shifts">
                    <ReloadOutlined className="card-icon" style={{ color: "#fff", fontSize: 24 }} />
                </div>
            ),
        },
    ]

    const workedData = analyticsData?.charts?.workedHours || []
    const breakData = analyticsData?.charts?.breakTime || []

    const disabledFutureDates = (current) => current && current > moment().endOf("day")

    return (
        <div style={{ padding: 24, background: "#fff" }}>
            {/* Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 16,
                    marginBottom: 24,
                }}
            >
                {/* Left Section: Title + Subtitle */}
                <div style={{ flex: 1, minWidth: 300 }}>
                    <h1 className="timesheet-title" style={{ marginBottom: 4 }}>
                        {translations[language]?.employeeAnalyticsHeading}
                    </h1>
                    <p className="timesheet-subtitle" style={{ margin: 0 }}>
                        {translations[language].employeAnalyticsParagraph}
                    </p>
                </div>

                {/* Middle Section: Date Range Picker */}
                <div className="date-employee-filters" >

                    {/* Middle Section: Date Range Picker */}
                    <div style={{ flexShrink: 0 }}>

                        <label style={{ marginBottom: 4 }}>{translations[language].dateRange}</label>

                        <RangePicker
                            style={{ borderRadius: 6, height: 40 }}
                            value={dateRange}
                            onChange={(values) => values && setDateRange(values)}
                            format={dateFormat}
                            allowClear={false}
                            disabledDate={disabledFutureDates}
                            className="date-range-picker"

                        />
                    </div>

                    {/* Right Section: Employee Select (only for admins) */}
                    {user?.role === "admin" || user?.role === "superAdmin" ? (
                        <div style={{ display: "flex", flexDirection: "column", marginLeft: 16 }} className="employee-select-container">

                            <label style={{ marginBottom: 4 }}>
                                {translations[language].employee}
                            </label>
                            <Select
                                className='employee-select-dropdown'
                                mode="multiple"
                                value={selectedOption}
                                placeholder={translations[language].selectEmployees}
                                onChange={(value) => setSelectedOption(value)}
                                allowClear
                                showSearch
                                style={{ width: 200, height: 40 }}
                                optionFilterProp="children"
                            >
                                {employeeNameList?.map((emp, index) => (
                                    <Select.Option key={index} value={emp?.id}>
                                        {emp?.user_name}
                                    </Select.Option>
                                ))}
                            </Select>

                        </div>
                    ) : null}

                </div>
            </div>


            {/* Cards */}
            <div className="home-card-div">
                <div
                    style={{
                        display: "grid",
                        gap: 16,
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    }}
                    className="dashboard-grid"
                >
                    {cards.map((card, index) => (
                        <div key={index} className="dashboard-card">
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 8,
                                }}
                            >
                                {card.icon}
                            </div>
                            <h3 style={{ margin: "8px 0", fontSize: "15px" }}>{card.title}</h3>
                            <p style={{ fontSize: 18, fontWeight: 600 }}>{card.value}</p>
                        </div>
                    ))}
                </div>
            </div>

            <Divider />

            {/* Charts */}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }} className="charts-container">
                {/* Worked Hours */}
                <Card
                    title={
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <ClockCircleOutlined style={{ color: "#1890ff" }} />
                            {translations[language]?.workedHours}
                        </span>
                    }
                    bordered={false}
                    style={{
                        flex: 1,
                        borderRadius: 10,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                        width: 400,
                    }}
                    className="graph-card"

                >
                    {workedData?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={workedData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip content={<CustomWorkedTooltip />} />
                                <Legend verticalAlign="bottom" align="center" />
                                <Line
                                    type="monotone"
                                    dataKey="worked_hours"
                                    name={translations[language]?.workedHours}
                                    stroke="#1890ff"
                                    strokeWidth={2}
                                    dot={{ r: 4 }}
                                />
                            </LineChart>

                        </ResponsiveContainer>
                    ) : (
                        <div
                            style={{
                                height: 200,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                            }}
                        >
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <span
                                        style={{
                                            color: "#999",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 6,
                                            textAlign: "center",
                                        }}
                                    >
                                        <ClockCircleOutlined style={{ color: "#1890ff" }} />
                                        {translations[language]?.noWorkedHoursAvailable}
                                    </span>
                                }
                            />
                        </div>

                    )}
                </Card>

                {/* Break Time */}
                <Card
                    title={
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <CoffeeOutlined style={{ color: "#fa8c16" }} />
                            {translations[language]?.dailyBreakTime}

                        </span>
                    }
                    bordered={false}
                    style={{
                        flex: 1,
                        borderRadius: 10,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                        width: 400,
                    }}

                    className="graph-card"
                >
                    {breakData?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={breakData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis domain={[0, 100]} />
                                <Tooltip content={<CustomBreakTooltip />} cursor={false} />
                                <Legend />
                                <Bar dataKey="break_minutes" name={translations[language]?.breakMinutes} fill="#fa8c16" barSize={40} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div
                            style={{
                                height: 200,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                            }}
                        >
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <span
                                        style={{
                                            color: "#999",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 6,
                                            textAlign: "center",
                                        }}
                                    >
                                        <CoffeeOutlined style={{ color: "#fa8c16" }} />
                                        {translations[language]?.noBreakAvailable}

                                    </span>
                                }
                            />
                        </div>

                    )}
                </Card>
            </div>
        </div >
    )
}

export default Analytics
